import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import {
  DownloadSimple,
  CheckCircle,
  ArrowRight,
  WarningCircle,
} from "@phosphor-icons/react";
import { trackEvent } from "@/lib/analytics";

// Public landing-style page for downloading the free Excel planner.
// Reached at /piano-excel. Reachable without login/registration.
// The 4-phase sticky stepper is NOT shown here (Header already hides it
// outside PHASE_ROUTES), only the standard top chrome.
// Download strategy — the file lives in /frontend/public/ and is served
// at the root (e.g. https://exchangeplan.app/ExchangePlan_Tool.xlsx), so
// the primary CTA uses a plain <a download> link that works on desktop
// and standard mobile browsers. For in-app browsers (Instagram/Facebook
// WebView) that silently ignore the `download` attribute, we expose a
// secondary "open in a new tab" fallback link right below the button.
const EXCEL_FILE = "/ExchangePlan_Tool.xlsx";
const APP_URL_WITH_UTM =
  "/organizza?utm_source=pagina_excel&utm_medium=sito&utm_campaign=piano_partenza";

export default function PianoExcel() {
  const { t } = useApp();
  const navigate = useNavigate();
  const copy = t.pianoExcel;

  const bullets = [copy.bullet1, copy.bullet2, copy.bullet3];

  const handleDownloadClick = () => {
    // Fire-and-forget: the browser handles the download via the <a> tag.
    // No preventDefault — Plausible gets the event via its own XHR while
    // the download starts in parallel.
    trackEvent("Excel Download");
  };

  const handleGoToAppClick = (e) => {
    e.preventDefault();
    // Use trackEvent's callback pattern so Plausible has a chance to
    // flush before React Router unmounts the current page.
    trackEvent("Excel To App Clicked", undefined, () => {
      navigate(APP_URL_WITH_UTM);
    });
  };

  return (
    <div className="min-h-screen overflow-x-hidden">
      <section className="max-w-3xl mx-auto px-5 pt-8 sm:pt-10 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-3 font-sans">
            {t.app.name}
          </p>
          <h1
            data-testid="piano-excel-title"
            className="font-display font-black text-3xl sm:text-5xl lg:text-6xl text-ink leading-[1.1] tracking-tight"
          >
            {copy.title}
          </h1>
          <p
            data-testid="piano-excel-subtitle"
            className="mt-3 sm:mt-4 text-ink-soft text-base sm:text-lg max-w-xl leading-relaxed"
          >
            {copy.subtitle}
          </p>
        </motion.div>

        {/* Bullets */}
        <motion.ul
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          data-testid="piano-excel-bullets"
          className="mt-5 sm:mt-6 space-y-2.5 sm:space-y-3"
        >
          {bullets.map((text, idx) => (
            <li
              key={idx}
              className="flex items-start gap-3 text-ink text-sm sm:text-base leading-relaxed"
            >
              <CheckCircle
                size={22}
                weight="duotone"
                className="text-terracotta shrink-0 mt-0.5"
              />
              <span>{text}</span>
            </li>
          ))}
        </motion.ul>

        {/* Primary CTA — plain anchor so the browser handles the download
            natively (works on desktop + most mobile browsers). On devices
            where `download` is ignored (iOS Safari in some cases, in-app
            browsers), the file still opens and can be saved from there. */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-6 sm:mt-8"
        >
          <a
            data-testid="piano-excel-download-btn"
            href={EXCEL_FILE}
            download="ExchangePlan_Tool.xlsx"
            onClick={handleDownloadClick}
            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto rounded-full bg-terracotta hover:bg-terracotta-hover text-white font-sans font-bold text-base sm:text-lg px-6 sm:px-8 py-3.5 sm:py-4 shadow-[0_8px_24px_rgba(224,122,95,0.3)] transition-colors"
          >
            <DownloadSimple size={22} weight="bold" />
            <span>{copy.downloadBtn}</span>
          </a>

          {/* Fallback link for in-app browsers (Instagram / Facebook) that
              strip the download attribute. target=_blank forces a real tab
              where the user can tap "..." → Save / Share. */}
          <p className="mt-3 text-xs sm:text-sm text-ink-soft/80 font-sans">
            <a
              data-testid="piano-excel-fallback-link"
              href={EXCEL_FILE}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:text-ink transition-colors"
            >
              {copy.fallbackLink}
            </a>
          </p>
        </motion.div>

        {/* Prominent indicative-deadlines notice.
            Soft amber background drawn from the "sand" family already used
            elsewhere; distinct enough to be noticed, warm enough to stay
            on-palette. Icon + bolded lead keep the reading rhythm clear. */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          data-testid="piano-excel-notice"
          className="mt-5 sm:mt-6 rounded-2xl border border-amber-300/70 bg-amber-50 px-4 sm:px-5 py-3.5 sm:py-4 flex items-start gap-3"
        >
          <WarningCircle
            size={24}
            weight="duotone"
            className="text-amber-600 shrink-0 mt-0.5"
          />
          <p className="text-ink text-sm sm:text-base leading-relaxed font-sans">
            <strong className="font-bold">{copy.noticeBold}</strong>
            {copy.noticeRest}
          </p>
        </motion.div>

        {/* App card — full-content-width, clearly actionable but softer
            than the primary download button: outlined pill instead of
            solid terracotta, slightly tinted surface-alt background.
            Reads as the "second option" without feeling like an afterthought. */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          data-testid="piano-excel-app-card"
          className="mt-6 sm:mt-8 rounded-3xl border border-border bg-surface-alt/60 px-5 sm:px-7 py-5 sm:py-6"
        >
          <h2 className="font-display font-bold text-xl sm:text-2xl text-ink tracking-tight">
            {copy.appCardTitle}
          </h2>
          <p className="mt-2 text-ink-soft text-sm sm:text-base leading-relaxed">
            {copy.appCardBody}
          </p>
          <a
            data-testid="piano-excel-goto-app"
            href={APP_URL_WITH_UTM}
            onClick={handleGoToAppClick}
            className="mt-4 inline-flex items-center justify-center gap-2 w-full sm:w-auto rounded-full border-2 border-terracotta bg-white text-terracotta hover:bg-terracotta hover:text-white font-sans font-bold text-base px-6 py-3 transition-colors"
          >
            <span>{copy.appCardCta}</span>
            <ArrowRight size={18} weight="bold" />
          </a>
        </motion.div>
      </section>
    </div>
  );
}
